import { useNavigate, useParams } from 'react-router';
import { useAppSelector } from '../../hooks/reduxToolkit';
import { NotFound } from '../../screens/notFound';
import { ExpenseRow } from '../../components/expenseTable';
import { AIPhoneCallWidget } from '../../components/providers/AIPhoneCallWidget';
import { ProviderAuthorizationCard } from '../../components/providers/ProviderAuthorizationCard';
import { ProviderConversationsPanel } from '../../components/providers/ProviderConversationsPanel';
import { useCallback, useState } from 'react';
import { useFeature } from '../../providers/featureFlags';
import { present } from '../../../lib/util/present';
import { selectConversationsWithNestedDataForProvider, findItemizedBillConversation, type ConversationWithMessagesAndPhoneCalls } from '../../reduxToolkit/conversationsSelectors';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { useClient, useSession } from '../../providers/supabase';
import { normalizePhoneNumber } from '../../../lib/util/normalizePhoneNumber';

import './show.scss';

export function ProviderShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useClient();
  const isAiPhoneCallingEnabled = useFeature('ai_phone_calling');
  const session = useSession();
  const signInEmail = session?.user?.email ?? '';

  const provider = useAppSelector((state) => state.providers?.providers.find((p) => p.id === id));

  // AI calling is allowed by default; a provider is only blocked once an admin
  // explicitly disables its number (revoked_at set). The RLS policy only
  // exposes disabled rows, so the absence of a row means the provider is enabled.
  const [isWhitelisted, setIsWhitelisted] = useState(true);
  useAsyncEffect(async () => {
    const normalizedPhone = normalizePhoneNumber(provider?.billing_phone);
    if (!normalizedPhone) { return }

    const result = await client.from('provider_phone_numbers')
      .select('revoked_at')
      .eq('normalized_phone', normalizedPhone)
      .maybeSingle();

    if (result.error) { throw result.error }

    setIsWhitelisted(!present(result.data?.revoked_at))
  }, [provider?.billing_phone])

  // Get conversation data from Redux
  const conversations = useAppSelector((state) => selectConversationsWithNestedDataForProvider(state, provider?.id || ''));

  // Get the conversation for a given expense. Keyed on the incident, not the expense --
  // see findItemizedBillConversation.
  const allExpensesById = useAppSelector((state) => state.expenses.expenses);
  const getConversationForExpense = useCallback((expenseId: string): ConversationWithMessagesAndPhoneCalls | null => {
    const expense = allExpensesById.find((e) => e.id === expenseId);
    return findItemizedBillConversation(conversations, expense, provider?.id) || null;
  }, [conversations, allExpensesById, provider?.id]);
  
  const expensesWithOpenItemizedBillTodos = useAppSelector((state) => {
    const allExpenses = state.expenses.expenses
      .filter((e) => e.provider_id === id)
    
    // Find all open TODOs with type 'itemized-bill' for expenses
    const openItemizedBillTodos = state.todos.todos.filter((todo) => 
      todo.todo_type === 'itemized-bill' && 
      todo.record_type === 'expense' && 
      !todo.completed_at && 
      !todo.deleted_at
    )
    
    // Get the expense IDs that have open itemized-bill TODOs
    const expenseIdsWithOpenTodos = openItemizedBillTodos.map(todo => todo.record_id)
    
    // Filter expenses to only include those with open itemized-bill TODOs
    return allExpenses.filter((expense) => 
      expenseIdsWithOpenTodos.includes(expense.id)
    )
  })
  
  if (!provider || !id) {
    return <NotFound />;
  }
  
  let googleMapsUrl = undefined
  if (provider.street_address) {
    googleMapsUrl = `https://www.google.com/maps/search/${provider.street_address}`
  }

  const [street, ...rest] = provider.street_address?.split(',') || []
  const formattedAddress = provider.street_address ? `${street}\n${rest.join(',').trim()}` : undefined

  return (
    <div className="provider-show">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{provider.name}</h1>
            <button 
              className="btn btn-outline-primary"
              onClick={() => navigate(`/providers/${id}/edit`)}
            >
              Edit
            </button>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Contact Information</h5>
              <div className="row">
                <div className="col-3 provider-show__contact-info-label">Phone</div>
                <div className="col-7 provider-show__contact-info">
                  {provider.billing_phone}
                </div>
                <div className="col-2 provider-show__contact-info-icon">
                  {provider.billing_phone && <a href={`tel:${provider.billing_phone}`}>
                    <i className="material-icons">phone</i>
                  </a>}
                </div>
              </div>

              <div className="row">
                <div className="col-3 provider-show__contact-info-label">Email</div>
                <div className="col-7 provider-show__contact-info">
                  {provider.billing_email}
                </div>
                <div className="col-2 provider-show__contact-info-icon">
                  {provider.billing_email && <a href={`mailto:${provider.billing_email}`}>
                    <i className="material-icons">mail</i>
                  </a>}
                </div>
              </div>

              <div className="row">
                <div className="col-3 provider-show__contact-info-label">Your email</div>
                <div className="col-9 provider-show__contact-info">
                  {provider.user_email_on_file
                    ? provider.user_email_on_file
                    : <span className="text-muted">{signInEmail} (sign-in default)</span>}
                </div>
              </div>

              <div className="row">
                <div className="col-3 provider-show__contact-info-label">Website</div>
                <div className="col-9 provider-show__contact-info">
                  {provider.website && <a href={provider.website} target="_blank" rel="noopener noreferrer">
                    {provider.website}
                  </a>}
                </div>
              </div>

              <div className="row">
                <div className="col-3 provider-show__contact-info-label">Address</div>
                <div className="col-7 provider-show__contact-info">
                  {formattedAddress &&
                    <pre className="mb-0">{formattedAddress}</pre>
                  }
                </div>
                <div className="col-2 provider-show__contact-info-icon">
                  {googleMapsUrl && <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                    <i className="material-icons">public</i>
                  </a>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <ProviderConversationsPanel
            providerId={id}
            isAuthorized={present(provider.communication_authorized_at)}
            conversations={conversations}
            showViewAllLink
          />
        </div>
      </div>

      <div className="container-fluid">
        {expensesWithOpenItemizedBillTodos.length > 0 && <>
          <h5>Expenses Missing Itemized Bills:</h5>
          <div className="expense-table">
            {expensesWithOpenItemizedBillTodos
              .slice()
              .sort((a, b) => {
                if (!present(a.date)) return -1;
                if (!present(b.date)) return 1;
                return Date.parse(b.date) - Date.parse(a.date);
              })
              .map((expense) => (
                <div key={expense.id} className="mb-3" style={{ marginBottom: '1.5rem' }}>
                  <ExpenseRow
                    expense={expense}
                    displayOptions={{
                      hideProvider: true
                    }}
                  />
                  {/* AI Phone Call Widget - shown at bottom of card, full width */}
                  {isAiPhoneCallingEnabled && (
                    <div className="row" style={{ marginTop: '-0.5em' }}>
                        <AIPhoneCallWidget
                          expenseId={expense.id}
                          provider={provider}
                          isWhitelisted={isWhitelisted}
                          existingConversation={getConversationForExpense(expense.id) || undefined}
                        />
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>}
      </div>

      {/* Authorization card always at the bottom */}
      <div className="row mt-4">
        <div className="col-12">
          <ProviderAuthorizationCard 
            providerId={id}
          />
        </div>
      </div>
    </div>
  );
} 
